import React, { useEffect, useState } from "react";
import Layout, { PageHeader } from "@/components/Layout";
import StatusBadge from "@/components/StatusBadge";
import { api, formatApiError } from "@/lib/api";
import { Plus, PencilSimple, Trash, HardDrives } from "@phosphor-icons/react";

const VENDORS = ["veeam", "acronis", "datto", "backblaze", "duplicati", "rsync", "windows-backup", "other"];

const EMPTY = {
  name: "", client_id: "", device: "", vendor: "other",
  expected_every_hours: 24, schedule_cron: "",
  sender_match: "", subject_match: "",
  notify_emails: "", enabled: true,
};

export default function Jobs() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [j, c] = await Promise.all([api.get("/jobs"), api.get("/clients")]);
      setItems(j.data); setClients(c.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing("new"); setError(""); };
  const openEdit = (j) => {
    setForm({
      name: j.name, client_id: j.client_id || "", device: j.device || "",
      vendor: j.vendor || "other", expected_every_hours: j.expected_every_hours || 24,
      schedule_cron: j.schedule_cron || "", sender_match: j.sender_match || "",
      subject_match: j.subject_match || "",
      notify_emails: (j.notify_emails || []).join(", "),
      enabled: !!j.enabled,
    });
    setEditing(j); setError("");
  };

  const save = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const payload = {
        ...form,
        client_id: form.client_id || null,
        device: form.device || null,
        schedule_cron: form.schedule_cron || null,
        sender_match: form.sender_match || null,
        subject_match: form.subject_match || null,
        expected_every_hours: Number(form.expected_every_hours) || 24,
        notify_emails: form.notify_emails.split(",").map(s => s.trim()).filter(Boolean),
      };
      if (editing === "new") await api.post("/jobs", payload);
      else await api.put(`/jobs/${editing.id}`, payload);
      setEditing(null); await load();
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete job?")) return;
    await api.delete(`/jobs/${id}`); await load();
  };

  const clientName = (id) => clients.find(c => c.id === id)?.name || "—";

  return (
    <Layout>
      <PageHeader
        title="Backup jobs"
        subtitle="Define expected backups; we match incoming reports automatically"
        actions={
          <button onClick={openNew} data-testid="jobs-new-btn"
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-brand-cyan text-brand-navy hover:bg-brand-cyan/90 rounded-md font-semibold transition-colors">
            <Plus size={14} weight="bold" /> New job
          </button>
        }
      />
      <div className="p-6">
        {loading ? <div className="text-sm text-slate-500">loading…</div>
         : items.length === 0 ? (
          <div className="sw-card border-dashed p-16 text-center">
            <HardDrives size={36} weight="duotone" className="text-brand-cyan mx-auto mb-3" />
            <h3 className="font-display text-lg font-bold tracking-tight mb-1">No jobs yet</h3>
            <p className="text-sm text-slate-400 mb-4">Define a backup job to start monitoring it.</p>
            <button onClick={openNew} data-testid="jobs-empty-cta"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-brand-cyan text-brand-navy hover:bg-brand-cyan/90 rounded-md font-semibold">
              <Plus size={14} weight="bold" /> Add backup job
            </button>
          </div>
        ) : (
          <div className="sw-card overflow-hidden">
            <table className="w-full text-sm" data-testid="jobs-table">
              <thead className="bg-background-deep/60">
                <tr className="text-[10px] uppercase tracking-[0.15em] text-slate-400">
                  <th className="text-left py-3 px-4">Status</th>
                  <th className="text-left py-3 px-4">Job</th>
                  <th className="text-left py-3 px-4">Client</th>
                  <th className="text-left py-3 px-4">Vendor</th>
                  <th className="text-left py-3 px-4">Expected every</th>
                  <th className="text-left py-3 px-4">Last run</th>
                  <th className="text-right py-3 px-4 w-32">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((j) => (
                  <tr key={j.id} className="border-t border-border hover:bg-white/5">
                    <td className="py-3 px-4"><StatusBadge status={j.last_status} /></td>
                    <td className="py-3 px-4">
                      <div className="text-white" data-testid={`job-name-${j.id}`}>{j.name}</div>
                      {j.device && <div className="text-[11px] text-slate-500">{j.device}</div>}
                    </td>
                    <td className="py-3 px-4 text-slate-300">{clientName(j.client_id)}</td>
                    <td className="py-3 px-4 text-xs uppercase text-slate-300">{j.vendor}</td>
                    <td className="py-3 px-4 text-xs text-slate-400">{j.expected_every_hours}h</td>
                    <td className="py-3 px-4 text-xs text-slate-400">
                      {j.last_run_at ? new Date(j.last_run_at).toLocaleString() : "never"}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <button onClick={() => openEdit(j)} data-testid={`job-edit-${j.id}`}
                        className="p-1.5 text-slate-300 hover:text-white"><PencilSimple size={14} /></button>
                      <button onClick={() => remove(j.id)} data-testid={`job-delete-${j.id}`}
                        className="p-1.5 text-slate-300 hover:text-red-400 ml-1"><Trash size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/70 z-30 flex items-center justify-center p-6" onClick={() => setEditing(null)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()}
            className="sw-card w-full max-w-xl p-6 space-y-3 max-h-[90vh] overflow-y-auto" data-testid="job-form">
            <h3 className="font-display text-lg font-bold tracking-tight">{editing === "new" ? "New backup job" : "Edit backup job"}</h3>
            <div className="grid grid-cols-2 gap-3">
              <FieldJ label="Name" required><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="job-name-input" /></FieldJ>
              <FieldJ label="Device / server"><input value={form.device} onChange={(e) => setForm({ ...form, device: e.target.value })} data-testid="job-device-input" /></FieldJ>
              <FieldJ label="Client">
                <select value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} data-testid="job-client-select">
                  <option value="">(unassigned)</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </FieldJ>
              <FieldJ label="Vendor">
                <select value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} data-testid="job-vendor-select">
                  {VENDORS.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </FieldJ>
              <FieldJ label="Expected every (hours)"><input type="number" min="1" value={form.expected_every_hours} onChange={(e) => setForm({ ...form, expected_every_hours: e.target.value })} data-testid="job-expected-input" /></FieldJ>
              <FieldJ label="Cron (optional)"><input value={form.schedule_cron} placeholder="0 2 * * *" onChange={(e) => setForm({ ...form, schedule_cron: e.target.value })} data-testid="job-cron-input" /></FieldJ>
              <FieldJ label="Match sender contains"><input value={form.sender_match} placeholder="reports@veeam.example" onChange={(e) => setForm({ ...form, sender_match: e.target.value })} data-testid="job-sender-input" /></FieldJ>
              <FieldJ label="Match subject contains"><input value={form.subject_match} placeholder="fileserver01" onChange={(e) => setForm({ ...form, subject_match: e.target.value })} data-testid="job-subject-input" /></FieldJ>
              <FieldJ label="Notify emails (comma-sep)" wide><input value={form.notify_emails} placeholder="oncall@msp.com, alerts@msp.com" onChange={(e) => setForm({ ...form, notify_emails: e.target.value })} data-testid="job-notify-input" /></FieldJ>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-200 pt-2">
              <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} data-testid="job-enabled-checkbox" />
              Enabled
            </label>
            {error && <div className="text-xs text-red-400">{error}</div>}
            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <button type="button" onClick={() => setEditing(null)} data-testid="job-cancel-btn"
                className="px-3 py-1.5 text-sm border border-border hover:bg-white/5 rounded-md">Cancel</button>
              <button type="submit" data-testid="job-save-btn"
                className="px-3 py-1.5 text-sm bg-brand-cyan text-brand-navy hover:bg-brand-cyan/90 rounded-md font-semibold">Save</button>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
}

function FieldJ({ label, children, wide, required }) {
  return (
    <div className={wide ? "col-span-2" : ""}>
      <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400 mb-1.5">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {React.cloneElement(children, {
        className: "w-full bg-background-deep border border-border rounded px-3 py-2 text-sm focus:outline-none focus:border-brand-cyan transition-colors",
      })}
    </div>
  );
}
